import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { BRAND } from "@/lib/brand";

interface WhiteLabelConfig {
  logo_url: string | null;
  cor_primaria: string | null;
  nome_empresa: string | null;
  dominio_customizado: string | null;
  ativo: boolean;
}

// Converte hex (#RRGGBB) para string de componentes HSL usada pelo Tailwind CSS
// Formato retornado: "H S% L%" (sem hsl())
function hexToHslComponents(hex: string): string | null {
  const clean = hex.replace("#", "");
  if (!/^[A-Fa-f0-9]{6}$/.test(clean)) return null;

  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function useWhiteLabel() {
  const { user } = useAuth();

  const { data: config, isLoading } = useQuery({
    queryKey: ["white-label", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("white_label_config")
        .select("logo_url, cor_primaria, nome_empresa, dominio_customizado, ativo")
        .eq("user_id", user.id)
        .eq("ativo", true)
        .maybeSingle();
      return (data as WhiteLabelConfig | null) ?? null;
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
  });

  // Aplicar CSS variable --sidebar-primary quando cor_primaria estiver configurada
  useEffect(() => {
    if (config?.cor_primaria) {
      const hsl = hexToHslComponents(config.cor_primaria);
      if (hsl) {
        document.documentElement.style.setProperty("--sidebar-primary", hsl);
      }
    }
    return () => {
      document.documentElement.style.removeProperty("--sidebar-primary");
    };
  }, [config?.cor_primaria]);

  // Atualizar <title> da página
  useEffect(() => {
    if (config?.nome_empresa) {
      document.title = config.nome_empresa;
    }
    return () => {
      document.title = BRAND;
    };
  }, [config?.nome_empresa]);

  return {
    logoUrl: config?.logo_url ?? null,
    nomeEmpresa: config?.nome_empresa ?? null,
    corPrimaria: config?.cor_primaria ?? null,
    dominio: config?.dominio_customizado ?? null,
    isActive: !!(config?.ativo),
    isLoading,
  };
}
